// config.ts
import { getEnvironment } from "./environment-checks";
var UndefinedError = class extends Error {
  constructor(key) {
    super(`Env var ${key} is required but not defined.`);
  }
};
var ClientAccessError = class extends Error {
  constructor(key) {
    super(`Config value "${String(key)}" is only accessible on the server.`);
  }
};
var Config = class {
  constructor(configKey, config) {
    this.configKey = configKey;
    this.definitions = { ...config };
    this.parsed = getEnvironment() === "node" ? this.loadServer() : this.loadClient();
  }
  loadClient() {
    const configKey = `adornisConfig_${this.configKey}`;
    const configObj = window[configKey];
    if (!configObj)
      throw new Error(
        `Config with key ${configKey} was not transferred to the client. Please make sure to include the config in the indexRenderer config array or make sure it is not imported on the client.`
      );
    const results = {};
    for (const [key, def] of Object.entries(this.definitions)) {
      if (!def.client)
        continue;
      const value = configObj[def.name];
      results[key] = this.parseValue(value, key, def);
    }
    console.log(Object.keys(results), "loaded config");
    return results;
  }
  loadServer() {
    const results = {};
    for (const [key, def] of Object.entries(this.definitions)) {
      const value = def.env ? process.env[def.name] : void 0;
      results[key] = this.parseValue(value, key, def);
    }
    console.log(Object.keys(results), "loaded config");
    return results;
  }
  parseValue(value, key, def) {
    try {
      console.debug(`loading env var ${def.name} => ${key}`);
      if (!def.env)
        return def.value;
      if (def.required) {
        if (!value)
          throw new UndefinedError(def.name);
        const parsed = def.parse?.(value);
        return parsed === void 0 ? value : parsed;
      }
      if (value !== void 0) {
        const parsed = def.parse?.(value);
        return parsed !== void 0 ? parsed : value;
      }
      return def.defaultVal;
    } catch (error) {
      console.warn(error);
      if (!def.env || def.required) {
        throw error;
      }
      return def.defaultVal;
    }
  }
  exportClientEnv() {
    const env = {};
    for (const [key, def] of Object.entries(this.definitions)) {
      if (def.client)
        env[def.name] = this.parsed[key];
    }
    delete env.ENVIRONMENT;
    return env;
  }
  exportClientScript() {
    return `window["adornisConfig_${this.configKey}"] = ${JSON.stringify(this.exportClientEnv())};`;
  }
  exportLegacyCompatClientScript() {
    return `window.A_CONFIG = { ROOT_URL: '${this.parsed.ROOT_URL}', DEVELOPMENT: '${this.parsed.DEPLOYMENT === "development"}', CLIENT: 'true' };`;
  }
  get(key) {
    if (getEnvironment() !== "node" && !this.definitions[key]?.client)
      throw new ClientAccessError(key);
    return this.parsed[key];
  }
};
export {
  ClientAccessError,
  Config,
  UndefinedError
};
